import { StyledText } from "./StyledText";
import { Link } from "react-router-dom";
import { ArrowRight, Mail } from "lucide-react";
import { useSite } from "../../lib/site";
import { href } from "../../lib/i18n";
import { Img, Socials } from "./Common";
import { Countdown } from "./Campaign";

function Signature({ className }) {
  const { site } = useSite();
  if (site.profile?.signature_image)
    return <Img src={site.profile.signature_image} alt={site.profile?.name} className={`h-28 sm:h-36 w-auto ${className}`} />;
  return <h1 data-testid="hero-signature-name" className={`font-signature signature-draw leading-[0.9] ${className}`}>{site.profile?.name}</h1>;
}

function InfoItem({ icon: Icon, label, children, testid }) {
  return (
    <div className="flex items-center gap-4" data-testid={testid}>
      <span className="w-14 h-14 shrink-0 rounded-full bg-white/5 border border-site-border flex items-center justify-center text-s-primary"><Icon size={20} strokeWidth={1.5} /></span>
      <div className="text-sm">
        <p className="text-[0.68rem] font-semibold uppercase tracking-[0.2em] text-s-text mb-1">{label}</p>
        <div className="text-s-muted space-y-0.5 [&_a]:underline [&_a]:underline-offset-4 hover:[&_a]:text-s-primary">{children}</div>
      </div>
    </div>
  );
}

function NormalHero() {
  const { site, lang, t, ui, subtitle } = useSite();
  const p = site.profile || {};
  const g = site.general || {};
  const parts = (p.name || "").split(" ");
  const aboutPage = site.menu.flatMap((m) => [m, ...(m.children || [])]).find((m) => m.kind === "page");
  return (
    <section data-testid="hero-section" className="relative -mt-20 pt-20 min-h-[100svh] flex flex-col overflow-hidden">
      <Img src={p.blog_photo || p.photo} alt={p.name} data-testid="hero-portrait" loading="eager"
        className="hero-photo absolute inset-y-0 right-0 w-full lg:w-[62%] h-full object-cover object-[center_25%]" />
      <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, var(--s-bg) 30%, color-mix(in srgb, var(--s-bg) 70%, transparent) 55%, color-mix(in srgb, var(--s-bg) 10%, transparent) 100%)" }} aria-hidden />
      <div className="absolute inset-x-0 bottom-0 h-56" style={{ background: "linear-gradient(0deg, var(--s-bg), transparent)" }} aria-hidden />
      <div className="relative flex-1 max-w-7xl w-full mx-auto px-5 sm:px-8 flex items-center py-16">
        <div className="max-w-2xl rise">
          {subtitle && <p className="eyebrow mb-8" data-testid="hero-tagline">{subtitle}</p>}
          <h1 className="font-heading text-6xl sm:text-7xl lg:text-[6.5rem] leading-[0.98] tracking-tight" data-testid="hero-signature-name">
            <span className="text-gold">{parts.slice(0, -1).join(" ") || parts[0]}</span>{parts.length > 1 && <><br />{parts[parts.length - 1]}</>}
          </h1>
          <p className="mt-8 font-heading italic text-2xl sm:text-[1.7rem] leading-snug text-s-text/90 max-w-xl">{t(p.intro)}</p>
          <div className="mt-12 flex flex-wrap gap-4">
            <Link to={href(lang, "news")} data-testid="hero-cta-primary"
              className="inline-flex items-center gap-3 rounded-[var(--s-radius)] bg-s-primary text-s-onprimary px-8 py-4 text-sm font-semibold hover:brightness-110 transition-[filter]">
              {ui.latestNews} <ArrowRight size={16} />
            </Link>
            {aboutPage && (
              <Link to={href(lang, "page", aboutPage.slug)} data-testid="hero-cta-secondary"
                className="inline-flex items-center rounded-[var(--s-radius)] border border-site-text/40 px-8 py-4 text-sm font-semibold hover:border-site-primary hover:text-s-primary transition-colors">
                {ui.whoAmI}
              </Link>
            )}
          </div>
        </div>
      </div>
      <div className="relative max-w-7xl w-full mx-auto px-5 sm:px-8 pb-14 flex flex-wrap gap-8 items-center justify-between rise" style={{ animationDelay: ".3s" }}>
        {g.email && <InfoItem icon={Mail} label={ui.contact} testid="hero-contact-info"><a href={`mailto:${g.email}`}>{g.email}</a></InfoItem>}
        <Socials socials={p.socials} />
      </div>
    </section>
  );
}

function CampaignHero() {
  const { site, lang, t, ui } = useSite();
  const p = site.profile || {};
  const c = site.campaign || {};
  return (
    <section data-testid="hero-section" className="relative overflow-hidden">
      <div className="absolute -right-40 top-10 w-[42rem] h-[42rem] rounded-full blur-3xl opacity-30 bg-s-primary" aria-hidden />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-12 sm:pt-16 pb-20 grid lg:grid-cols-[1.25fr_1fr] gap-12 items-end">
        <div className="rise pb-6">
          <p className="inline-flex items-center gap-2 rounded-full border border-site-border px-4 py-1.5 text-xs font-semibold tracking-widest uppercase mb-8">
            <span className="w-2 h-2 rounded-full bg-s-primary animate-pulse" /> {t(p.title)}
          </p>
          <StyledText as="h1" testid="hero-slogan" className="font-display font-black uppercase tracking-tight text-5xl sm:text-6xl lg:text-7xl leading-[0.95]" style={c.slogan_style} text={t(c.slogan)} />
          <Signature className="text-6xl sm:text-7xl text-s-accent mt-6" />
          <p className="mt-6 text-lg text-s-muted max-w-xl" data-testid="hero-intro">{t(p.intro_campaign)}</p>
          <div className="mt-10 flex flex-wrap gap-4">
            {c.volunteer_enabled && (
              <a href="#gonullu" data-testid="hero-cta-primary" className="inline-flex items-center gap-2 rounded-full bg-s-primary text-s-onprimary px-8 py-4 font-bold uppercase tracking-wide hover:-translate-y-0.5 transition-transform">
                {ui.volunteer} <ArrowRight size={18} />
              </a>
            )}
            <Link to={href(lang, "contact")} data-testid="hero-cta-secondary" className="inline-flex items-center rounded-full border border-site-border px-8 py-4 font-bold uppercase tracking-wide hover:bg-s-surface transition-colors">
              {ui.contact}
            </Link>
          </div>
          {c.election_date && <div className="mt-12"><Countdown compact /></div>}
        </div>
        <div className="relative rise" style={{ animationDelay: ".15s" }}>
          <Img src={p.photo} alt={p.name} data-testid="hero-portrait" loading="eager" className="relative w-full aspect-[4/5] object-cover rounded-[2rem] shadow-2xl" />
          <div className="absolute -bottom-4 -left-4 right-10 h-3 bg-s-primary rounded-full" aria-hidden />
        </div>
      </div>
    </section>
  );
}

export default function Hero() {
  const { site } = useSite();
  return site.theme?.mode === "adaylik" ? <CampaignHero /> : <NormalHero />;
}
